import { useCallback, useEffect, useState } from 'react'
import { getApprovalQueue, approveApprovalQueueItem, dismissApprovalQueueItem } from '../services/approvalQueueApi'
import type { ApprovalQueueItem } from '../types'

export function useApprovalQueue(workspaceId: string) {
  const [items, setItems] = useState<ApprovalQueueItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Per-item in-flight/error state, keyed by item id — one item resolving must not
  // block or hide errors for the rest of the list.
  const [resolvingIds, setResolvingIds] = useState<Record<string, boolean>>({})
  const [resolveErrors, setResolveErrors] = useState<Record<string, string>>({})

  const load = useCallback(() => {
    // Lets a caller (e.g. SettingsPage, for a sidebar badge count) hold this
    // hook before the workspace has resolved, instead of every caller having
    // to wait to mount it.
    if (!workspaceId) {
      setLoading(false)
      setItems([])
      return () => {}
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    getApprovalQueue(workspaceId)
      .then((result) => { if (!cancelled) setItems(result) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the approval queue') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [workspaceId])

  useEffect(() => load(), [load])

  const resolve = useCallback(
    async (itemId: string, action: 'approve' | 'dismiss', edits?: { subject?: string; body?: string }) => {
      setResolvingIds((prev) => ({ ...prev, [itemId]: true }))
      setResolveErrors((prev) => {
        const copy = { ...prev }
        delete copy[itemId]
        return copy
      })
      try {
        if (action === 'approve') await approveApprovalQueueItem(workspaceId, itemId, edits)
        else await dismissApprovalQueueItem(workspaceId, itemId)
        // Resolved items drop out of the pending list — the backend only ever
        // returns pending items from GET, so a resolved one has nothing more to show.
        setItems((prev) => prev.filter((i) => i.id !== itemId))
      } catch (err) {
        setResolveErrors((prev) => ({ ...prev, [itemId]: err instanceof Error ? err.message : 'Failed' }))
      } finally {
        setResolvingIds((prev) => {
          const copy = { ...prev }
          delete copy[itemId]
          return copy
        })
      }
    },
    [workspaceId],
  )

  const approve = useCallback(
    (itemId: string, edits?: { subject?: string; body?: string }) => resolve(itemId, 'approve', edits),
    [resolve],
  )
  const dismiss = useCallback((itemId: string) => resolve(itemId, 'dismiss'), [resolve])

  return { items, loading, error, retry: load, resolvingIds, resolveErrors, approve, dismiss }
}
